"use client";

import { POLYMARKET_GEOBLOCK_URL } from "@/constants/polymarket";
import { useQuery } from "@tanstack/react-query";

export interface PolymarketGeoblockResponse {
  blocked: boolean;
  ip: string;
  country: string;
  region: string;
}

/**
 * Hook to check if the user is geoblocked from Polymarket.
 * Calls Polymarket's API directly from the browser to ensure correct IP detection.
 * (Proxying through our backend doesn't work because Polymarket ignores forwarded IP headers)
 */
export const usePolymarketGeoblock = () => {
  return useQuery({
    queryKey: ["polymarket-geoblock"],
    queryFn: async (): Promise<PolymarketGeoblockResponse> => {
      const response = await fetch(POLYMARKET_GEOBLOCK_URL, {
        method: "GET",
        headers: {
          "Cache-Control": "no-store",
          "Pragma": "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(`Geoblock check failed: ${response.status}`);
      }

      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - geoblock status doesn't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};
